import { Axios } from "api/axios";
import WarehouseAutocomplete from "Common/Components/InputComponent/WarehouseAutocomplete";
import ActionButton from "components/Buttons/ActionButton";
import CancelBackButton from "components/Buttons/CancelBackButton";
import SubmitButton from "components/Buttons/SubmitButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import LoadingSpinner from "components/LoaderErrorWrapper/LoadingSpinner";
import PageContainer from "components/PageContainer/PageContainer";
import { useFormik } from "formik";
import { api } from "helpers/api_url";
import ConfigureDispatchItems from "pages/Warehouse/Dispatch/ConfigureDispatchItems/ConfigureDispatchItems";
import React, { useCallback, useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import QRScanner from "utils/QRScanner";
import * as Yup from "yup";
import { useWarehouseSelection } from "CustomHooks/useWarehouseSelection";

import { Autocomplete } from "@mui/material";
import BootstrapTextField from "Common/BootstrapTextField";
import { autocompleteDropdownStyles } from "Constants/Style";
import { fetchLabourDropdownForForms } from "slices/Labour/thunk";
import {
  createIssueToLabour,
  fetchIssueToLabourById,
  updateIssueToLabour,
} from "slices/Warehouse/Dispatch/IssueToLabour/thunk";
import { showConfirm, showCustomSwal } from "utils/swalHelper";
import { useAutoMinimizeSidebar } from "CustomHooks/useAutoMinimizeSidebar";

interface StockTransferFormValues {
  dispatch_date: string;
  challan_no: string;
  issue_from: string;
  issue_from_id: string;
  labour_name: string;
  labour_id: string;
  upload_document: File | null;
  existing_document_file: string | null;
  remark: string;
}

interface BagData {
  bag_no: string;
  inventory_item_id: string | number;
  total_quantity: number;
  bag_item_dispatch_quantity: number;
}

interface DispatchItem {
  id: string;
  inventory_item_id?: string;
  item_id?: string;
  itemName: string;
  itemCode: string;
  category: string;
  category_id?: string;
  subCategory: string;
  sub_category_id?: string;
  unit: string;
  unit_id?: string;
  bags: BagData[]; // Array of bags instead of single bag_no
  dispatch_quantity: number;
  available_quantity?: number; // Available quantity for scanned items
  isScanned?: boolean; // Flag to identify scanned items
  remarks: string;
}

const IssueToLabourForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const [dispatchItems, setDispatchItems] = useState<DispatchItem[]>([]);
  const [labourOptions, setLabourOptions] = useState<any[]>([]);
  const [showScanner, setShowScanner] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(true);

  // Use custom hook for warehouse selection
  const { warehouseOptions, selectedWarehouse } = useWarehouseSelection({
    sessionStorageKey: "WarehouseIdDispatch",
    autoSelectFirst: true,
  });

  // Auto-minimize sidebar on tablet screens
  useAutoMinimizeSidebar();

  // Get stock transfer state from Redux
  const { loading: isLoadingItem, creating } = useSelector(
    (state: any) => state.StockTransfer
  );

  const validationSchema = Yup.object({
    dispatch_date: Yup.string().required("Please select Dispatch Date"),
    issue_from: Yup.string().required("Please select Issue From"),
    challan_no: Yup.string(),
    labour_name: Yup.string().required("Please select Labour"),
    remark: Yup.string(),
    upload_document: Yup.mixed().nullable(),
  });

  // Get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const formik = useFormik<StockTransferFormValues>({
    initialValues: {
      dispatch_date: getCurrentDate(),
      challan_no: "",
      issue_from: selectedWarehouse?.warehouse_name || "",
      issue_from_id: selectedWarehouse
        ? String(selectedWarehouse.warehouse_id)
        : "",
      labour_name: "",
      labour_id: "",
      upload_document: null as File | null,
      existing_document_file: null as string | null,
      remark: "",
    },

    validationSchema: validationSchema,
    onSubmit: async (values) => {
      // Validate items
      if (dispatchItems.length === 0) {
        showCustomSwal({
          icon: "error",
          text: "Please add at least one item",
        });
        return;
      }

      // Validate quantity for each item
      const invalidQuantity = dispatchItems.some((item, index) => {
        // Validate dispatch quantity
        if (
          item.dispatch_quantity === undefined ||
          item.dispatch_quantity === null ||
          item.dispatch_quantity <= 0
        ) {
          Swal.fire({
            icon: "error",
            text: `Dispatch Quantity must be greater than 0 for row ${
              index + 1
            }`,
          });
          return true;
        }
        return false;
      });

      if (invalidQuantity) return;

      // Build FormData
      const formData = buildFormData(values, dispatchItems);
      formData.forEach((value, key) => {
        console.log(key, value);
      });
      try {
        if (isEditMode && id) {
          await dispatch(updateIssueToLabour({ id, formData })).unwrap();
          console.log("Issue to labour data edited", formData);
          sessionStorage.setItem("WarehouseItemInwardTab", "4");
          toast.success("Stock transfer dispatch updated successfully");
        } else {
          await dispatch(createIssueToLabour(formData)).unwrap();
          sessionStorage.setItem("WarehouseItemInwardTab", "4");
          toast.success("Issue to labour dispatch created successfully");
          // console.log("Issue to labour data created", formData);
        }
        navigate("/warehouse-dispatch");
      } catch (err: any) {
        handleSubmitError(err);
      }
    },
  });

  // Reset document fields when switching to create mode or when id changes
  useEffect(() => {
    if (!isEditMode || !id) {
      formik.setFieldValue("upload_document", null);
      formik.setFieldValue("existing_document_file", null);
      // Reset file input
      const fileInput = document.getElementById(
        "uploadDocInput"
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = "";
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // If edit mode, fetch dispatch data
        if (isEditMode && id) {
          const dispatchData = await dispatch(
            fetchIssueToLabourById(id)
          ).unwrap();
          // Format dispatch date for date input
          const dispatchDate = dispatchData.dispatch_date
            ? new Date(dispatchData.dispatch_date).toISOString().split("T")[0]
            : getCurrentDate();

          // Set form values
          formik.setFieldValue("dispatch_date", dispatchDate);
          formik.setFieldValue("challan_no", dispatchData.challan_no || "");
          formik.setFieldValue("remark", dispatchData.remark || "");
          formik.setFieldValue(
            "issue_from",
            dispatchData.issued_from_warehouse?.warehouse_name || ""
          );
          formik.setFieldValue(
            "issue_from_id",
            dispatchData.issued_from_warehouse?.warehouse_id || ""
          );
          formik.setFieldValue(
            "labour_name",
            dispatchData?.labour?.labour_name || ""
          );
          formik.setFieldValue(
            "labour_id",
            dispatchData?.labour?.labour_id || ""
          );

          console.log("Fetched dispatch data:", dispatchData);
          // Always clear document fields first to prevent stale data
          formik.setFieldValue("existing_document_file", null);
          formik.setFieldValue("upload_document", null);
          // Reset file input
          const fileInput = document.getElementById(
            "uploadDocInput"
          ) as HTMLInputElement;
          if (fileInput) {
            fileInput.value = "";
          }

          // Handle existing document - only set if document exists and is valid
          // Check if document exists, is a non-empty string, and is a valid URL
          const documentUrl = dispatchData.document;
          if (
            documentUrl &&
            typeof documentUrl === "string" &&
            documentUrl.trim() !== ""
          ) {
            // Set the document URL (can be relative or absolute)
            formik.setFieldValue("existing_document_file", documentUrl.trim());
            console.log("Set existing document file:", documentUrl.trim());
          } else {
            console.log("No valid document found in API response");
          }

          // Transform dispatch items to form format
          if (
            dispatchData.dispatch_items &&
            Array.isArray(dispatchData.dispatch_items)
          ) {
            const transformedItems: DispatchItem[] =
              dispatchData.dispatch_items.map((item: any, index: number) => {
                // Parse bags from bags field or item_details.bags
                let bags: BagData[] = [];
                // First check bags field (primary location)
                if (
                  item.bags &&
                  Array.isArray(item.bags) &&
                  item.bags.length > 0
                ) {
                  bags = item.bags.map((bag: any) => ({
                    bag_no: bag.bag_no || "",
                    inventory_item_id: bag.inventory_item_id || "",
                    total_quantity: Number(bag.total_quantity || 0),
                    bag_item_dispatch_quantity: Number(
                      bag.dispatch_quantity ||
                        bag.issued_quantity ||
                        bag.bag_item_dispatch_quantity ||
                        0
                    ),
                  }));
                }
                // Fallback to item_details.bags if bags field is empty
                else if (
                  item.item_details?.bags &&
                  Array.isArray(item.item_details.bags) &&
                  item.item_details.bags.length > 0
                ) {
                  bags = item.item_details.bags.map((bag: any) => ({
                    bag_no: bag.bag_no || "",
                    inventory_item_id: bag.inventory_item_id || "",
                    total_quantity: Number(bag.total_quantity || 0),
                    bag_item_dispatch_quantity: Number(
                      bag.dispatch_quantity ||
                        bag.issued_quantity ||
                        bag.bag_item_dispatch_quantity ||
                        0
                    ),
                  }));
                }

                return {
                  id: `item-${index}-${Date.now()}`,
                  item_id: item.master_item?.item_id || "",
                  itemName: item.item_name || "",
                  itemCode: item.item_code || "",
                  category:
                    item.category?.category_name || item.category_name || "",
                  category_id: item.category?.category_id || "",
                  subCategory:
                    item.sub_category?.sub_category_name ||
                    item.sub_category_name ||
                    "",
                  sub_category_id: item.sub_category?.sub_category_id || "",
                  unit: item.unit?.unit_name || item.unit_name || "",
                  unit_id: item.unit?.unit_id || "",
                  bags: bags,
                  dispatch_quantity: Number(
                    item.dispatch_quantity || item.total_quantity || 0
                  ),
                  remarks: item.remark || "-",
                };
              });
            setDispatchItems(transformedItems);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load form data");
      } finally {
        setIsFormLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id, isEditMode]);

  //   Labour dropdown
  useEffect(() => {
    const fetchSupplierOptions = async () => {
      try {
        const response = await dispatch(fetchLabourDropdownForForms()).unwrap();
        setLabourOptions(response || []);
      } catch (error) {
        console.error("Error fetching supplier options:", error);
      }
    };
    fetchSupplierOptions();
  }, [dispatch]);

  // Auto-select transfer from warehouse when selectedWarehouse is available
  useEffect(() => {
    if (warehouseOptions.length > 0 && selectedWarehouse && !isEditMode) {
      const warehouseIdToUse = String(selectedWarehouse.warehouse_id);
      const warehouseNameToUse = selectedWarehouse.warehouse_name;

      // Set the warehouse if found and not already set
      if (
        warehouseIdToUse &&
        warehouseNameToUse &&
        formik.values.issue_from_id !== warehouseIdToUse
      ) {
        formik.setFieldValue("issue_from", warehouseNameToUse);
        formik.setFieldValue("issue_from_id", warehouseIdToUse);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWarehouse, warehouseOptions, isEditMode]);

  // Helper function to build FormData
  const buildFormData =
    //   useCallback(
    (values: StockTransferFormValues, items: DispatchItem[]): FormData => {
      const formData = new FormData();
      formData.append("dispatch_date", values.dispatch_date);
      formData.append("challan_no", values.challan_no || "");
      formData.append("remarks", values.remark || "");
      formData.append("issued_from_warehouse_id", values.issue_from_id || "");
      formData.append("issue_from_warehouse_name", values.issue_from || "");
      formData.append("labour_id", values.labour_id || "");
      formData.append("labour_name", values.labour_name || "");
      //  formData.append("transfer_to_warehouse_id", values.transfer_to_id || "");

      // Append file if it exists, or existing document if in edit mode
      if (values.upload_document && values.upload_document instanceof File) {
        formData.append("document", values.upload_document);
      } else if (isEditMode && values.existing_document_file) {
        formData.append("existing_document", values.existing_document_file);
      }

      // Transform items to dispatch_items format
      const transformedItems = items.map((item: DispatchItem) => ({
        category_id: item.category_id || "",
        category_name: item.category || "",
        sub_category_id: item.sub_category_id || "",
        sub_category_name: item.subCategory || "",
        item_id: item.item_id || "",
        item_name: item.itemName,
        item_code: item.itemCode,
        unit_id: item.unit_id || "",
        unit_name: item.unit,
        bags: item.bags || [], // Array of bags: [{bag_no, inventory_item_id, dispatch_quantity}]
        dispatch_quantity: Number(item.dispatch_quantity) || 0, // Use dispatch_quantity as total_quantity for submission
        remarks: item.remarks || "",
      }));

      formData.append("dispatch_items", JSON.stringify(transformedItems));
      return formData;
    };
  //     [isEditMode]
  //   );

  // Helper function to handle submit errors
  const handleSubmitError = useCallback((err: any) => {
    // Handle Redux thunk errors (from rejectWithValue)
    const payload = err?.payload || err;
    const responseData =
      err?.response?.data || payload?.responseData || payload;

    const validationErrors =
      responseData?.errors ||
      payload?.errors ||
      payload?.responseData?.data?.errors ||
      payload?.responseData?.errors;

    if (Array.isArray(validationErrors) && validationErrors.length > 0) {
      try {
        Swal.fire({
          icon: "error",
          title:
            validationErrors.length > 1
              ? "Validation Errors"
              : "Validation Error",
          html: `<div style="text-align: left;">${validationErrors
            .map((err: string) => `• ${err}`)
            .join("<br>")}</div>`,
          confirmButtonText: "OK",
          allowOutsideClick: false,
          allowEscapeKey: true,
        });
      } catch (swalError) {
        toast.error(validationErrors.join("; "));
      }
    } else {
      // Prioritize 'error' field over 'message' field as it contains the specific error
      const errorMessage =
        responseData?.error ||
        err?.response?.data?.error ||
        payload?.error ||
        err?.error ||
        responseData?.message ||
        err?.response?.data?.message ||
        payload?.message ||
        err?.message ||
        "Failed to process stock transfer dispatch. Please try again.";
      toast.error(errorMessage);
    }
  }, []);

  // Handle QR scan result
  const handleQRScanResult = async (scannedValue: any) => {
    // Validate that transfer from warehouse is selected
    if (
      !formik.values.issue_from_id ||
      formik.values.issue_from_id.trim() === ""
    ) {
      toast.error("Please select Issue From warehouse before scanning items");
      setShowScanner(false);
      return;
    }

    let inventoryItemId: string | null = null;

    // Extract inventory_item_id from scanned value
    if (typeof scannedValue === "string") {
      inventoryItemId = scannedValue;
    } else if (typeof scannedValue === "object" && scannedValue !== null) {
      inventoryItemId =
        scannedValue.inventory_item_id || scannedValue.id || scannedValue;
    }

    if (!inventoryItemId) {
      toast.error("Invalid QR code. Could not extract inventory item ID.");
      return;
    }

    try {
      // Get transfer_from_warehouse_id to send as query parameter
      const issueFromWarehouseId = formik.values.issue_from_id;

      const response = await Axios.get(
        `${api.warehouse.dispatch.stock_transfer.inventory_item_by_id}${inventoryItemId}`,
        {
          params: {
            warehouse_id: issueFromWarehouseId,
          },
        }
      );

      if (response.data?.success && response.data?.data) {
        const itemData = response.data.data;

        // Check if item already exists in the list
        const existingItem = dispatchItems.find(
          (item) => item.inventory_item_id === inventoryItemId
        );

        if (existingItem) {
          toast.warning("Item already added to the list");
          return;
        }

        // Create new dispatch item from scanned inventory item
        const availableQty = Number(itemData.available_quantity) || 0;

        // Check if available quantity is 0
        if (availableQty === 0) {
          toast.error("Scanned bag has 0 items");
          return;
        }

        const bagData: BagData = {
          bag_no: itemData.bag_no || "",
          inventory_item_id: itemData.inventory_item_id,
          total_quantity: availableQty,
          bag_item_dispatch_quantity: availableQty,
        };
        const newItem: DispatchItem = {
          id: `qr-${Date.now()}`,
          inventory_item_id: itemData.inventory_item_id,
          item_id: itemData.item.item_id,
          itemName: itemData.item.item_name,
          itemCode: itemData.item.item_code,
          category: itemData.item.category_name || "",
          category_id: itemData.item.category_id,
          subCategory: itemData.item.sub_category_name || "",
          sub_category_id: itemData.item.sub_category_id,
          unit: itemData.item.unit_name || "",
          unit_id: itemData.item.unit_id,
          bags: [bagData], // Array with single bag for scanned items
          dispatch_quantity: availableQty, // Set dispatch quantity to available by default
          available_quantity: availableQty,
          isScanned: true,
          remarks: "",
        };

        setDispatchItems((prev) => [...prev, newItem]);
        toast.success("Item added successfully from QR scan");
      } else {
        throw new Error("Failed to fetch inventory item");
      }
    } catch (error: any) {
      console.error("Error fetching inventory item:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch inventory item. Please try again.";

      // Check if error is about warehouse mismatch
      if (errorMessage.includes("not present in the selected warehouse")) {
        Swal.fire({
          icon: "error",
          title: "Warehouse Mismatch",
          text: errorMessage,
          confirmButtonText: "OK",
        });
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const handleItemsUpdated = (items: DispatchItem[]) => {
    setDispatchItems(items);
  };

  return (
    <PageContainer>
      <FormHeader
        title={isEditMode ? "Edit Issue to Labour" : "Create Issue to Labour"}
        firstLabel="Dispatch"
        secondLabel={
          isEditMode ? "Edit Issue to Labour" : "Create Issue to Labour"
        }
        handleArrowClick={() => navigate("/warehouse-dispatch")}
      />

      <hr />

      {isFormLoading ? (
        <LoadingSpinner />
      ) : (
        <Form autoComplete="off" onSubmit={formik.handleSubmit}>
          <Row>
            {/* Dispatch Date */}
            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>
                  Dispatch Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="dispatch_date"
                  value={formik.values.dispatch_date}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.dispatch_date &&
                    formik.touched.dispatch_date
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.dispatch_date}
                </Form.Control.Feedback>
              </div>
            </Col>

            {/* Challan No */}
            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>Challan No</Form.Label>
                <Form.Control
                  type="text"
                  name="challan_no"
                  placeholder="Enter Challan No."
                  value={formik.values.challan_no}
                  onChange={formik.handleChange}
                />
              </div>
            </Col>

            {/* Transfer From */}
            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>
                  Issue From <span className="text-danger">*</span>
                </Form.Label>

                <WarehouseAutocomplete
                  options={warehouseOptions}
                  value={formik.values.issue_from_id}
                  showWarehouseType={true}
                  formik={formik}
                  formikFieldId="issue_from_id"
                  formikFieldName="issue_from"
                  name="issue_from"
                  inputSx={{
                    background: "#FFF",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ced4da",
                      },
                    },
                  }}
                  onChange={async (
                    newWarehouseId,
                    newWarehouseName,
                    selectedOption
                  ) => {
                    const currentWarehouseId = formik.values.issue_from_id;
                    const currentWarehouseName = formik.values.issue_from;

                    // Check if warehouse is being changed/removed and items exist
                    if (
                      dispatchItems.length > 0 &&
                      currentWarehouseId &&
                      currentWarehouseId !== newWarehouseId
                    ) {
                      const result = await Swal.fire({
                        title: "Change Warehouse?",
                        text: "If you change the warehouse, the added items will be removed. Do you want to continue?",
                        icon: "warning",
                        showCancelButton: true,
                        confirmButtonText: "Yes, Change Warehouse",
                        cancelButtonText: "Cancel",
                        confirmButtonColor: "#d33",
                        cancelButtonColor: "#3085d6",
                      });

                      if (result.isConfirmed) {
                        // Clear all items
                        setDispatchItems([]);
                        // Update warehouse
                        formik.setFieldValue("issue_from_id", newWarehouseId);
                        formik.setFieldValue("issue_from", newWarehouseName);
                        // Update sessionStorage
                        if (newWarehouseId) {
                          sessionStorage.setItem(
                            "WarehouseIdDispatch",
                            String(newWarehouseId)
                          );
                        }
                      } else {
                        // Revert to previous selection
                        setTimeout(() => {
                          formik.setFieldValue(
                            "issue_from_id",
                            currentWarehouseId
                          );
                          formik.setFieldValue(
                            "issue_from",
                            currentWarehouseName
                          );
                        }, 0);
                        return;
                      }
                    } else {
                      // No items or first time selection - just update
                      formik.setFieldValue("issue_from_id", newWarehouseId);
                      formik.setFieldValue("issue_from", newWarehouseName);
                      // Update sessionStorage
                      if (newWarehouseId) {
                        sessionStorage.setItem(
                          "WarehouseIdDispatch",
                          String(newWarehouseId)
                        );
                      }
                    }
                    formik.setFieldTouched("issue_from", true);
                  }}
                  onBlur={() => formik.setFieldTouched("issue_from", true)}
                />
              </div>
            </Col>

            {/* Labour Name Autocomplete */}
            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>
                  Labour Name <span className="text-danger">*</span>
                </Form.Label>

                <Autocomplete
                  options={labourOptions}
                  getOptionLabel={(option: any) => option?.labour_name || ""}
                  // ✅ Match by ID, not name
                  value={
                    labourOptions.find(
                      (opt: any) => opt.labour_id === formik.values.labour_id
                    ) || null
                  }
                  onChange={(event, selectedOption: any) => {
                    formik.setFieldValue(
                      "labour_id",
                      selectedOption?.labour_id || ""
                    );
                    formik.setFieldValue(
                      "labour_name",
                      selectedOption?.labour_name || ""
                    );
                    formik.setFieldTouched("labour_name", true);
                  }}
                  onBlur={() => formik.setFieldTouched("labour_name", true)}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select Labour"
                      name="labour_name"
                      error={
                        formik.touched.labour_name &&
                        Boolean(formik.errors.labour_name)
                      }
                      helperText={
                        formik.touched.labour_name && formik.errors.labour_name
                      }
                      fullWidth
                      size="small"
                      sx={{
                        background: "#FFF",
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&:hover fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#ced4da",
                          },
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt: any) => opt?.labour_name || ""
                    )
                  }
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ced4da",
                      },
                    },
                  }}
                />
              </div>
            </Col>

            {/* Document */}
            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>
                  Upload document
                  {/* <span className="text-danger">*</span> */}
                </Form.Label>

                {/* Custom styled box */}
                <div
                  className={`d-flex align-items-center justify-content-between p-2 border rounded ${
                    formik.errors.upload_document &&
                    formik.touched.upload_document
                      ? "border-danger"
                      : ""
                  }`}
                  style={{
                    height: "40px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    backgroundColor: "#fff",
                    borderColor:
                      formik.errors.upload_document &&
                      formik.touched.upload_document
                        ? "#dc3545"
                        : "#ced4da",
                  }}
                  onMouseEnter={(e) => {
                    if (
                      !formik.errors.upload_document ||
                      !formik.touched.upload_document
                    ) {
                      e.currentTarget.style.borderColor = "#80bdff";
                      e.currentTarget.style.backgroundColor = "#f8f9fa";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (
                      !formik.errors.upload_document ||
                      !formik.touched.upload_document
                    ) {
                      e.currentTarget.style.borderColor = "#ced4da";
                      e.currentTarget.style.backgroundColor = "#fff";
                    }
                  }}
                  onClick={() => {
                    document.getElementById("uploadDocInput")?.click();
                  }}
                >
                  <span
                    className="text-muted"
                    style={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      marginRight: "8px",
                      minWidth: 0,
                    }}
                    title={
                      formik.values.upload_document instanceof File
                        ? formik.values.upload_document.name
                        : formik.values.existing_document_file
                        ? formik.values.existing_document_file
                            .split("/")
                            .pop() || "Existing Document"
                        : "Click to upload document"
                    }
                  >
                    {formik.values.upload_document instanceof File ? (
                      formik.values.upload_document.name
                    ) : formik.values.existing_document_file ? (
                      <a
                        href={formik.values.existing_document_file}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-decoration-none text-primary"
                        style={{
                          cursor: "pointer",
                          display: "inline-block",
                          maxWidth: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={
                          formik.values.existing_document_file
                            .split("/")
                            .pop() || "Existing Document"
                        }
                      >
                        {formik.values.existing_document_file
                          .split("/")
                          .pop() || "Existing Document"}
                      </a>
                    ) : (
                      "Click to upload document"
                    )}
                  </span>

                  {/* Action buttons */}
                  <div
                    className="d-flex align-items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                    style={{ flexShrink: 0 }}
                  >
                    {formik.values.upload_document instanceof File ? (
                      <>
                        <i
                          className="bi bi-upload text-primary"
                          style={{
                            cursor: "pointer",
                            fontSize: "1rem",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            document.getElementById("uploadDocInput")?.click();
                          }}
                          title="Change document"
                        ></i>
                        <i
                          className="bi bi-x-circle text-danger"
                          style={{
                            cursor: "pointer",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            formik.setFieldValue("upload_document", null);
                            formik.setFieldValue(
                              "existing_document_file",
                              null
                            );
                            // Reset file input
                            const fileInput = document.getElementById(
                              "uploadDocInput"
                            ) as HTMLInputElement;
                            if (fileInput) {
                              fileInput.value = "";
                            }
                          }}
                          title="Remove document"
                        ></i>
                      </>
                    ) : formik.values.existing_document_file ? (
                      <>
                        <a
                          href={formik.values.existing_document_file}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          title="View document"
                        >
                          <i
                            className="bi bi-eye text-primary"
                            style={{
                              cursor: "pointer",
                              fontSize: "1rem",
                              transition: "transform 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "scale(1.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "scale(1)";
                            }}
                          ></i>
                        </a>
                        <i
                          className="bi bi-upload text-primary"
                          style={{
                            cursor: "pointer",
                            fontSize: "1rem",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            document.getElementById("uploadDocInput")?.click();
                          }}
                          title="Change document"
                        ></i>
                        <i
                          className="bi bi-x-circle text-danger"
                          style={{
                            cursor: "pointer",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            formik.setFieldValue("upload_document", null);
                            formik.setFieldValue(
                              "existing_document_file",
                              null
                            );
                            // Reset file input
                            const fileInput = document.getElementById(
                              "uploadDocInput"
                            ) as HTMLInputElement;
                            if (fileInput) {
                              fileInput.value = "";
                            }
                          }}
                          title="Remove document"
                        ></i>
                      </>
                    ) : (
                      <i
                        className="bi bi-upload"
                        style={{
                          cursor: "pointer",
                          fontSize: "1rem",
                          color: "#6c757d",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "#007bff";
                          e.currentTarget.style.transform = "scale(1.1)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#6c757d";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      ></i>
                    )}
                  </div>
                </div>

                {/* Hidden real file input */}
                <Form.Control
                  id="uploadDocInput"
                  type="file"
                  name="upload_document"
                  style={{ display: "none" }}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const file = event.currentTarget.files?.[0] || null;
                    formik.setFieldValue("upload_document", file);
                    // Clear existing_document_file when a new file is uploaded
                    if (file) {
                      formik.setFieldValue("existing_document_file", null);
                    }
                    formik.setFieldTouched("upload_document", true);
                  }}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.upload_document &&
                    formik.touched.upload_document
                  }
                />

                <Form.Control.Feedback type="invalid">
                  {formik.errors.upload_document}
                </Form.Control.Feedback>
              </div>
            </Col>

            {/* Remarks */}
            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>Remarks</Form.Label>
                <Form.Control
                  type="text"
                  name="remark"
                  placeholder="Enter Remarks"
                  value={formik.values.remark}
                  onChange={formik.handleChange}
                />
              </div>
            </Col>
          </Row>

          <hr />

          {/* Item Details */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="card-title mb-0">Item Details</h6>
              <ActionButton
                label="Scan"
                icon="bi bi-qr-code-scan"
                handleClick={() => {
                  if (
                    !formik.values.issue_from_id ||
                    formik.values.issue_from_id.trim() === ""
                  ) {
                    toast.error(
                      "Please select Transfer From warehouse before scanning items"
                    );
                    return;
                  }
                  setShowScanner(true);
                }}
                variant="primary"
                disabled={
                  !formik.values.issue_from_id ||
                  formik.values.issue_from_id.trim() === ""
                }
              />
            </div>
            {/* Transfer From Warehouse is not selected */}
            {(!formik.values.issue_from_id ||
              formik.values.issue_from_id.trim() === "") && (
              <div className="alert alert-warning mb-3" role="alert">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Please select Transfer From warehouse before scanning/adding
                items
              </div>
            )}
            {isLoadingItem && (
              <div className="text-center py-2">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading item from QR scan...</p>
              </div>
            )}
            <ConfigureDispatchItems
              key={formik.values.issue_from_id || "no-warehouse"}
              onItemsUpdated={handleItemsUpdated}
              initialItems={
                dispatchItems.length > 0 ? dispatchItems : undefined
              }
              showBagNo={true}
              transferFromWarehouseId={formik.values.issue_from_id}
              isEditMode={isEditMode}
            />
          </div>

          <div className="d-flex justify-content-center gap-3 py-3">
            <CancelBackButton
              label="Cancel"
              onClick={() => {
                showConfirm(
                  "Any unsaved changes will be lost.",
                  "Are you sure?",
                  "Yes",
                  "No"
                ).then((result) => {
                  if (result.isConfirmed) {
                    navigate("/warehouse-dispatch");
                  }
                });
              }}
            />

            <SubmitButton
              isSubmitting={formik.isSubmitting || creating}
              labels={{
                create: isEditMode ? "Update" : "Continue",
                submitting: isEditMode ? "Updating..." : "Creating...",
              }}
            />
          </div>
        </Form>
      )}

      <QRScanner
        show={showScanner}
        handleClose={() => setShowScanner(false)}
        onResult={handleQRScanResult}
      />
    </PageContainer>
  );
};

export default IssueToLabourForm;
